import { useEffect, useMemo } from 'react';
import { fg } from '@atlassian/jira-feature-gating';
import getMeta from '@atlassian/jira-get-meta';
import { useProductName } from '@atlassian/jira-tenant-context-controller/src/components/product-name/index.tsx';
import { useProject } from '@atlassian/jira-navigation-apps-sidebar-common/src/controllers/project/index.tsx';
import { createRouterSelector } from '@atlassian/react-resource-router';
import assetsFavicon from './assets/favicon-temp-assets.png';
import jiraFavicon from './assets/favicon-temp-family.png';
import jpdFavicon from './assets/favicon-temp-jpd.png';
import jsmFavicon from './assets/favicon-temp-jsm.png';
import csmFavicon from './assets/favicon-temp-csm.png';

export const FAVICON_METADATA_KEY = 'ajs-fspage-favicon-prefixed';
export const FAVICON_METADATA_KEY_FALLBACK = 'ajs-fspage-favicon';
export const USE_PROJECT_ICON_AS_FAVICON_METADATA_KEY = 'ajs-favicon-use-projecticon';

const useRouteGroup = createRouterSelector((state) => state.route.group);

const getCustomFavicon = () => {
	// Some instances might not have the prefixed metadata key, so we fallback to the original key
	const faviconPath = getMeta(FAVICON_METADATA_KEY) || getMeta(FAVICON_METADATA_KEY_FALLBACK);

	// Check if favicon is default or custom by matching the image path with pattern "favicon.*.ico"
	if (faviconPath) {
		const imagePath = faviconPath.split('/').at(-1);
		const defaultFaviconPattern = /^favicon.*\.ico$/;

		if (imagePath && !defaultFaviconPattern.test(imagePath)) {
			return faviconPath;
		}
	}

	return null;
};

/**
 * This is a temporary solution for replacing Jira's favicons with the new icons (T25).
 *
 * Favicon selection follows this priority order:
 * 1. Assets: If the user is on the Assets app
 * 2. Project favicon: If project-specific favicons are enabled
 * 3. Custom favicon: If user has set a custom favicon
 * 4. Product-specific defaults: Based on product type (JSM, JPD, CSM, or Jira)
 * 5. Fallback: Default favicon from backend
 */
export const useFaviconService = __SERVER__
	? () => undefined
	: () => {
			const routeGroup = useRouteGroup();

			const { data: project } = useProject();
			const isProjectFaviconEnabled = getMeta(USE_PROJECT_ICON_AS_FAVICON_METADATA_KEY) === 'true';

			const productName = useProductName();

			// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
			const iconEle = document.querySelector("link[rel='shortcut icon']");

			// Keep a copy of the default favicon
			const defaultFavicon = useMemo(
				() => (iconEle instanceof HTMLLinkElement ? iconEle.href : ''),
				[iconEle],
			);

			const customFavicon = getCustomFavicon();

			useEffect(() => {
				let faviconPath = defaultFavicon;

				if (routeGroup === 'assets' && fg('assets_as_an_app_v2')) {
					// Assets is currently a specific set of routes, and doesn't exist as a separate "product" as per `productName`.
					// Assets is moving towards being a platform outside of Jira (rendering its own Bifrost app etc), so it doesn't
					// make sense to register it as a Jira sub-product.
					if (customFavicon && fg('assets_nav_customisation_fix')) {
						faviconPath = customFavicon;
					} else {
						faviconPath = assetsFavicon;
					}
				} else if (isProjectFaviconEnabled && project) {
					faviconPath = project.avatarUrl;
				} else if (customFavicon) {
					faviconPath = customFavicon;
				} else if (productName === 'serviceDesk') {
					faviconPath = jsmFavicon;
				} else if (productName === 'product-discovery') {
					faviconPath = jpdFavicon;
				} else if (productName === 'customer-service') {
					faviconPath = csmFavicon;
				} else if (productName === 'jira' || productName === 'core' || productName === 'software') {
					faviconPath = jiraFavicon;
				}

				if (iconEle instanceof HTMLLinkElement) {
					iconEle.href = faviconPath;
				}

				// Cleanup: Restore default favicon when component unmounts
				return () => {
					if (iconEle instanceof HTMLLinkElement) {
						iconEle.href = defaultFavicon;
					}
				};
			}, [
				routeGroup,
				productName,
				defaultFavicon,
				iconEle,
				customFavicon,
				isProjectFaviconEnabled,
				project,
			]);
		};
